import { find, isArray, isObject, map, mapValues, transform } from "lodash";
import initTranslations from "../../app/i18n";
import i18nConfig, { NextJsI18NConfig } from "../../../next-i18next.config";

import { PAGES } from "@/internals/constants/pages";

import { TranslationKey } from "./types";
import { getSiteDomain } from "@/utils/site";

export interface TranslationVariable {
  propertyId: string;
  translations: {
    id: string;
    value: string;
  }[];
}

export type LocaleData = {
  page: String;
  language: String;
  variables?: TranslationVariable[];
};

type TranslatedData<T> = {
  data: T;
};

export const getLanguages = () => {
  const i18n = i18nConfig as NextJsI18NConfig;

  return i18n.locales;
};

export class LocaleDataFetcher<T> {
  page: String;
  language: String;
  variables?: TranslationVariable[];

  constructor({ page, language, variables }: LocaleData) {
    this.page = page || PAGES.NOT_FOUND;
    this.language = language;
    this.variables = variables || [];
  }

  domainFolder = getSiteDomain();

  async getData() {
    const data = (await import(`@/data/domain/${this.domainFolder}/content-types/${this.page}.ts`))
      .default;
    return {
      data,
    };
  }

  async getDataWithTranslations(): Promise<TranslatedData<T>> {
    const { t } = await initTranslations(this.language, [this.page]);
    const { data } = await this.getData();
    const translated = this.getTranslations(t, data) as T;

    return {
      data: translated,
    };
  }

  getTranslations = (translateFn: Function, data: any): any => {
    if (!data || !translateFn) return undefined;

    if (typeof data === "boolean") {
      return data;
    }

    if (isObject(data)) {
      return isArray(data)
        ? map(data, (dataValue) => this.getTranslations(translateFn, dataValue))
        : mapValues(data, (dataValue) => this.getTranslations(translateFn, dataValue));
    } else {
      const translationVariables = find(this.variables, { propertyId: data });
      const formatedTranslations =
        translationVariables &&
        transform(
          translationVariables.translations as TranslationKey[],
          (a, b) => {
            return (a[b.id as keyof TranslationKey] = b.value), a;
          },
          {} as TranslationKey
        );

      return formatedTranslations
        ? translateFn(data, formatedTranslations)
        : translateFn(data, { keySeparator: false, nsSeparator: false }) || data;
    }
  };

  updatePage({ page }: { page: string }) {
    this.page = page || PAGES.NOT_FOUND;
  }

  updateVariables({ variables }: { variables: TranslationVariable[] }) {
    this.variables = variables || [];
  }
}

export async function fetchData<T>(page: string, lang: string, variables?: TranslationVariable[]) {
  const dataFetcher = new LocaleDataFetcher<T>({
    page: page,
    language: lang,
    variables,
  });

  const { data } = await dataFetcher.getDataWithTranslations();

  return data;
}
